import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  hideDocumentProfile,
  loadDocumentProfile,
} from 'services/redux/documentProfileSlice';
import { RootState } from 'services/redux/store';

import { generateDocumentReportPDF } from '../../../../pages/Finance/Components/Financing/common/exportDocumentReport';
import { FieldVisibilityConfig } from '../../../../pages/Finance/Components/Financing/common/exportDocumentReport/types';
import { getCompanyLogo } from '../../../api/company';
import { expressGetCompanyLogo } from '../../../api/express';
import { getDocumentReport } from '../../../api/reports';
import { formatDate } from '../../../functions/utils';
import CollectionsSection from './components/CollectionsSection';
import ExportConfigDialog from './components/ExportConfigDialog';
import GeneralSection from './components/GeneralSection';
import StateSection from './components/StateSection';
import SummarySection from './components/SummarySection';

const DocumentProfile = () => {
  const dispatch = useDispatch();
  const { open, document, loading, error, title } = useSelector(
    (state: RootState) => state.documentProfile,
  );
  const user = useSelector((state: RootState) => state.auth.user);
  const [exporting, setExporting] = useState(false);
  const [showExportConfig, setShowExportConfig] = useState(false);

  useEffect(() => {
    if (open && document.id) {
      void loadDocumentProfile(dispatch, document.id);
    }
  }, [open, document.id, dispatch]);

  const handleClose = useCallback(() => {
    dispatch(hideDocumentProfile());
  }, [dispatch]);

  const handleExportClick = () => {
    setShowExportConfig(true);
  };

  const handleExportConfigClose = () => {
    setShowExportConfig(false);
  };

  const handleExportConfigConfirm = async (
    fieldVisibility: FieldVisibilityConfig,
  ) => {
    if (!document.id) return;

    setExporting(true);
    try {
      const response = await getDocumentReport(dispatch, document.id);
      const imageBase64 = user?.express
        ? await expressGetCompanyLogo(dispatch, user.clientId as string)
        : await getCompanyLogo(dispatch);
      await generateDocumentReportPDF(
        response,
        user?.name || '',
        imageBase64,
        fieldVisibility,
      );
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <Dialog
        maxWidth="lg"
        fullWidth
        open={open}
        onClose={handleClose}
        scroll="paper"
      >
        <Box
          sx={{
            p: 3,
            position: 'relative',
            maxHeight: '80vh',
            overflow: 'auto',
          }}
        >
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 1,
              }}
            >
              <CircularProgress size={60} />
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              pr: 6,
            }}
          >
            <Typography variant="h3">{title}</Typography>
            <Button
              variant="text"
              onClick={handleExportClick}
              disabled={exporting}
              sx={{
                color: 'primary.main',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              {exporting ? 'Exportando...' : 'Exportar a PDF'}
            </Button>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
            size="large"
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <GeneralSection
              businessId={document?.businessIdentifier || null}
              businessName={document?.businessName || null}
              debtorId={document?.stakeholderIdentifier || null}
              debtorName={document?.stakeholderName || null}
              operationNumber={document?.requestId || null}
              folio={document?.folio || null}
              state={document?.status || null}
              documentType={document?.type || null}
            />
            <Box sx={{ display: 'flex', gap: 3, width: '100%' }}>
              <Box sx={{ flex: 1 }}>
                <SummarySection
                  operationDate={
                    document.operationDate
                      ? formatDate(new Date(document.operationDate))
                      : null
                  }
                  businessRate={document.rate?.toString() || null}
                  documentAmount={document.amount || null}
                  interestCost={document.costRate || null}
                  paymentDate={
                    document.paymentDate
                      ? formatDate(new Date(document.paymentDate))
                      : null
                  }
                  advance={document.advance || null}
                  morosityRate={document.arrearRate || null}
                  netAmount={document.amountNet || null}
                  days={document.daysToCount || null}
                  invoiceAmount={document.invoiceAmount || null}
                  fundName={document.fundName || null}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <StateSection
                  overdueBalance={document?.arrearAmount || null}
                  principalBalance={
                    document?.documentRemainingAmountNet || null
                  }
                  totalBalance={document?.totalRemainingNet || null}
                  creditNoteAmount={document?.creditNoteAmount || null}
                />
              </Box>
            </Box>
            <CollectionsSection
              totalPaid={
                document?.amountConciliated !== undefined
                  ? (document.arrearConciliation || 0) +
                    (document.documentConciliation || 0)
                  : null
              }
              totalDiscounted={
                document?.documentAmountDiscounted !== undefined
                  ? document.documentAmountDiscounted
                  : null
              }
              paidOverdue={
                document?.arrearConciliation !== undefined
                  ? document.arrearConciliation
                  : null
              }
              paidPrincipal={
                document?.documentConciliation !== undefined
                  ? document.documentConciliation
                  : null
              }
              conciliationLog={document?.conciliationLog || []}
            />
          </Box>
        </Box>
      </Dialog>

      <ExportConfigDialog
        open={showExportConfig}
        onClose={handleExportConfigClose}
        onConfirm={handleExportConfigConfirm}
      />
    </>
  );
};

export default DocumentProfile;
